import _t from 'i18n!nls/premium-hub';

export type FilterBucket = {
  id: string;
  name: string;
};

// ---------------------------------------------------------------------------
// Cost (tuition) buckets
// ---------------------------------------------------------------------------
export const TUITION_NOT_AVAILABLE_BUCKET_ID = 'price-not-available';
export const TUITION_UNDER_15K = { id: 'under-15000', min: 0, max: 15000 };
export const TUITION_FROM_15K_TO_30K = { id: '15000-30000', min: 15000, max: 30000 };
export const TUITION_OVER_30K = { id: 'over-30000', min: 30000, max: Infinity };

type TuitionBucketRange = { id: string; min: number; max: number };

// Names are resolved lazily (via getTuitionBuckets/getTuitionBucketName) rather than
// baked into this array, since `_t()` must be called at render/use time -- calling it
// here at module-eval time would bake in whatever locale was active when this module
// was first imported, breaking SSR for any other locale sharing the same process.
const TUITION_BUCKET_RANGES: ReadonlyArray<TuitionBucketRange> = [
  TUITION_UNDER_15K,
  TUITION_FROM_15K_TO_30K,
  TUITION_OVER_30K,
];

export const getTuitionBucketId = (tuitionInUsd: number | null | undefined): string => {
  if (tuitionInUsd == null) {
    return TUITION_NOT_AVAILABLE_BUCKET_ID;
  }
  const bucket = TUITION_BUCKET_RANGES.find(({ min, max }) => tuitionInUsd >= min && tuitionInUsd < max);
  return bucket?.id ?? TUITION_NOT_AVAILABLE_BUCKET_ID;
};

const getTuitionBucketName = (id: string): string => {
  switch (id) {
    case TUITION_UNDER_15K.id:
      return _t('Under $15,000');
    case TUITION_FROM_15K_TO_30K.id:
      return _t('$15,000 - $30,000');
    case TUITION_OVER_30K.id:
      return _t('Over $30,000');
    default:
      return '';
  }
};

export const getTuitionBuckets = (): FilterBucket[] =>
  TUITION_BUCKET_RANGES.map(({ id }) => ({ id, name: getTuitionBucketName(id) }));

export const getTuitionNotAvailableBucket = (): FilterBucket => ({
  id: TUITION_NOT_AVAILABLE_BUCKET_ID,
  name: _t('Price not available'),
});

// ---------------------------------------------------------------------------
// Duration buckets
// ---------------------------------------------------------------------------

type DurationBucketRange = { id: string; minMonths: number; maxMonths: number };

export const DURATION_NOT_SPECIFIED_BUCKET_ID = 'duration-not-specified';
export const DURATION_UP_TO_12_MONTHS = { id: 'up-to-12-months', minMonths: 0, maxMonths: 12 };
export const DURATION_FROM_13_TO_24_MONTHS = { id: '13-24-months', minMonths: 12, maxMonths: 24 };
export const DURATION_OVER_24_MONTHS = { id: 'over-24-months', minMonths: 24, maxMonths: Infinity };

const DURATION_BUCKET_RANGES: ReadonlyArray<DurationBucketRange> = [
  DURATION_UP_TO_12_MONTHS,
  DURATION_FROM_13_TO_24_MONTHS,
  DURATION_OVER_24_MONTHS,
];

const YEAR_UNIT_PATTERN = /year|ano|año/;
const NUMBER_PATTERN = /\d+(\.\d+)?/g;

/**
 * Parses a free-text "at a glance" time frame string (e.g. "24 months",
 * "2 – 3 years", "24 meses.") into a number of months. Returns null when
 * no number can be found (e.g. missing data, or the literal placeholder
 * "Time frame" seen in some Contentful entries).
 *
 * When a string expresses a range (e.g. "12 – 36 months"), the LOWER bound is
 * used, since that represents the shortest a learner might realistically take
 * to complete the program.
 */
export const parseDurationToMonths = (timeFrame: string | null | undefined): number | null => {
  if (!timeFrame) {
    return null;
  }
  const normalized = timeFrame.toLowerCase();
  const numbers = normalized.match(NUMBER_PATTERN);
  if (!numbers || numbers.length === 0) {
    return null;
  }
  const lowerBound = Math.min(...numbers.map(Number));
  const isYears = YEAR_UNIT_PATTERN.test(normalized);
  return isYears ? lowerBound * 12 : lowerBound;
};

export const getDurationBucketId = (timeFrame: string | null | undefined): string => {
  const months = parseDurationToMonths(timeFrame);
  if (months == null) {
    return DURATION_NOT_SPECIFIED_BUCKET_ID;
  }
  const bucket = DURATION_BUCKET_RANGES.find(({ minMonths, maxMonths }) => months > minMonths && months <= maxMonths);
  return bucket?.id ?? DURATION_NOT_SPECIFIED_BUCKET_ID;
};

const getDurationBucketName = (id: string): string => {
  switch (id) {
    case DURATION_UP_TO_12_MONTHS.id:
      return _t('Up to 12 months');
    case DURATION_FROM_13_TO_24_MONTHS.id:
      return _t('13 - 24 months');
    case DURATION_OVER_24_MONTHS.id:
      return _t('Over 24 months');
    default:
      return '';
  }
};

export const getDurationBuckets = (): FilterBucket[] =>
  DURATION_BUCKET_RANGES.map(({ id }) => ({ id, name: getDurationBucketName(id) }));

export const getDurationNotSpecifiedBucket = (): FilterBucket => ({
  id: DURATION_NOT_SPECIFIED_BUCKET_ID,
  name: _t('Duration not specified'),
});
